import { Store, LineChart, ShoppingCart, Check, ArrowDown } from "lucide-react";
import { ACCENT, Eyebrow, H2, CtaLink, MarketplaceLogos } from "@/components/ecommerce/shared";

const SERVICES = [
  { key: "store", Icon: Store, name: "eCommerce Website Development", who: "For businesses that need a professional online store.",
    copy: "Build an online store designed around your products, customers and business model.",
    problem: "Solves: a store that isn't ready to sell — products, checkout, payments or delivery not properly set up.",
    caps: ["Storefront design & development", "Product catalogue setup", "Checkout, payments & delivery"], cta: "Build My Online Store", anchor: "#online-store" },
  { key: "strategy", Icon: LineChart, name: "eCommerce Strategy & Growth", who: "For businesses planning or improving how they sell online.",
    copy: "Develop a practical plan for your products, sales channels, customer acquisition and growth.",
    problem: "Solves: unclear direction, traffic without enough purchases and limited performance visibility.",
    caps: ["Channel & pricing strategy", "Customer acquisition planning", "Conversion & retention"], cta: "Plan My eCommerce Growth", anchor: "#strategy" },
  { key: "marketplace", Icon: ShoppingCart, name: "Marketplace Enablement", who: "For brands and retailers preparing to sell on marketplaces.",
    copy: "Get practical assistance preparing to sell on Amazon, Takealot and Makro.",
    problem: "Solves: difficult seller onboarding, catalogue preparation, listings and marketplace operations.",
    caps: ["Marketplace readiness", "Catalogue & listing preparation", "Operations & growth planning"], cta: "Get Marketplace Setup Help", anchor: "#marketplaces" },
];

export function EcomServices() {
  return (
    <section id="services" aria-labelledby="services-heading" data-testid="ecom-services-section" className="relative py-20 sm:py-28 bg-[#16120F] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow dark>eCommerce development and strategy</Eyebrow>
          <H2 id="services-heading" dark>Three Ways We Help Your Business Sell and Grow Online</H2>
        </div>
        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <article key={s.key} data-testid={`ecom-service-${s.key}`} className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 flex flex-col">
              <span className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: ACCENT }}><s.Icon className="h-6 w-6 text-white" /></span>
              <h3 className="mt-5 font-display text-2xl font-black tracking-tight">{s.name}</h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/70">{s.who}</p>
              <p className="mt-4 text-white/85 leading-relaxed">{s.copy}</p>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.problem}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {s.caps.map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-white/80"><Check className="h-4 w-4 shrink-0" style={{ color: ACCENT }} /> {c}</li>
                ))}
              </ul>
              <div className="mt-7 flex flex-col gap-3">
                <CtaLink label={s.cta} serviceKey={s.key} location={`ecom_services_${s.key}`} testid={`ecom-service-cta-${s.key}`} variant="light" />
                <a href={s.anchor} data-testid={`ecom-service-more-${s.key}`} className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white transition-colors">
                  See what's included <ArrowDown className="h-4 w-4" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Deliverables({ items, testid, cols = "sm:grid-cols-2" }) {
  return (
    <ul data-testid={testid} className={`grid ${cols} gap-x-6 gap-y-2.5`}>
      {items.map((d) => (
        <li key={d} className="flex items-start gap-2.5 text-[15px] text-zinc-700">
          <span className="mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${ACCENT}18`, color: ACCENT }}><Check className="h-3 w-3" strokeWidth={3} /></span>
          {d}
        </li>
      ))}
    </ul>
  );
}

const STORE_ITEMS = ["eCommerce website planning", "Storefront design and development", "Mobile-responsive layouts", "Product and category setup",
  "Product descriptions and imagery", "Product variations and options", "Shopping cart and checkout", "Supported payment integrations",
  "Delivery and shipping configuration", "Customer notifications", "Basic eCommerce analytics", "Technical SEO foundations", "Launch testing and handover"];

export function EcomOnlineStore() {
  return (
    <section id="online-store" aria-labelledby="online-store-heading" data-testid="ecom-online-store-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
        <div>
          <Eyebrow>eCommerce website development · online store setup</Eyebrow>
          <H2 id="online-store-heading">eCommerce Website Development for South African Businesses</H2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Your online store should make it easy for customers to discover products, understand what they're buying and complete a purchase.
          </p>
          <p className="mt-3 text-zinc-600 leading-relaxed">
            Rekisa builds eCommerce websites around your products and selling requirements. We help configure the essential components,
            from product catalogues and checkout to payments, delivery and customer communication.
          </p>
          <div className="mt-6 rounded-2xl bg-[#FBF7F2] border border-zinc-200 p-5 text-sm text-zinc-700 leading-relaxed">
            <span className="font-bold text-zinc-900">The whole purchase journey, not just the design. </span>
            We plan how customers find products, decide, pay and receive their order — then build the store around it.
          </div>
          <div className="mt-8"><CtaLink label="Request an Online Store Quote" serviceKey="store" location="ecom_online_store" testid="ecom-online-store-cta" /></div>
        </div>
        <div className="rounded-3xl border border-zinc-200 p-7">
          <h3 className="font-display text-xl font-black text-zinc-900 mb-5">Core deliverables</h3>
          <Deliverables items={STORE_ITEMS} testid="ecom-online-store-deliverables" />
          <p data-testid="ecom-online-store-note" className="mt-6 text-xs text-zinc-500 leading-relaxed">
            Platform expertise includes Ecwid, where relevant. Payment options such as PayFast and Yoco are configured when supported by the
            selected implementation. Shipping configuration depends on the available fulfilment services and integrations.
          </p>
        </div>
      </div>
    </section>
  );
}

const STRATEGY_ITEMS = ["eCommerce business model assessment", "Customer and market understanding", "Product positioning", "Pricing and margin considerations",
  "Channel selection", "Competitor analysis", "Online store strategy", "Marketplace strategy", "Customer acquisition planning",
  "Conversion optimisation", "Customer retention and repeat purchase journeys", "Performance measurement"];
const LOOP = ["Understand", "Build", "Measure", "Improve", "Grow"];

export function EcomStrategy() {
  return (
    <section id="strategy" aria-labelledby="strategy-heading" data-testid="ecom-strategy-section" className="relative py-20 sm:py-28 bg-[#FBF7F2] border-y border-zinc-200">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>eCommerce strategy consulting</Eyebrow>
          <H2 id="strategy-heading">Build an eCommerce Strategy That Supports Real Business Growth</H2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            A successful online business needs clarity about what it sells, who it sells to and how customers will discover and buy its products.
          </p>
          <p className="mt-3 text-zinc-600 leading-relaxed">
            Rekisa helps businesses develop practical eCommerce strategies that connect their business model, product offering, pricing,
            selling channels, marketing and customer experience.
          </p>
        </div>
        <ol data-testid="ecom-strategy-loop" className="mt-10 flex flex-wrap items-center gap-2">
          {LOOP.map((l, i) => (
            <li key={l} className="flex items-center gap-2">
              <span className="rounded-full bg-[#16120F] text-white px-4 py-2 text-sm font-bold">{l}</span>
              {i < LOOP.length - 1 && <span aria-hidden className="text-zinc-400">→</span>}
            </li>
          ))}
        </ol>
        <p className="mt-4 text-sm text-zinc-600 max-w-2xl">
          We develop a plan, implement the important components, measure what happens and improve the system over time. A strategy guides
          decisions — it does not guarantee sales.
        </p>
        <div className="mt-10 rounded-3xl border border-zinc-200 bg-white p-7">
          <h3 className="font-display text-xl font-black text-zinc-900 mb-5">Strategic services</h3>
          <Deliverables items={STRATEGY_ITEMS} testid="ecom-strategy-services" cols="sm:grid-cols-2 lg:grid-cols-3" />
        </div>
        <div className="mt-8"><CtaLink label="Request My eCommerce Strategy Plan" serviceKey="strategy" location="ecom_strategy" testid="ecom-strategy-cta" variant="dark" /></div>
      </div>
    </section>
  );
}

const MP_SERVICES = [
  ["Marketplace readiness", "Assess whether the business, products and operational processes are prepared for the selected marketplace."],
  ["Seller onboarding support", "Assist with the application and onboarding steps permitted by each marketplace."],
  ["Product catalogue preparation", "Prepare product information, categories, imagery, attributes and pricing details."],
  ["Listing creation and optimisation", "Help sellers create accurate, useful product listings that follow marketplace requirements."],
  ["Stock and fulfilment readiness", "Review inventory availability, delivery requirements and relevant fulfilment options."],
  ["Marketplace operations", "Support the setup of practical processes for managing products, orders and seller performance."],
  ["Marketplace growth planning", "Identify opportunities to improve listings, visibility and the customer experience."],
];

export function EcomMarketplaces() {
  return (
    <section id="marketplaces" aria-labelledby="marketplaces-heading" data-testid="ecom-marketplaces-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Marketplace seller setup · Takealot, Amazon, Makro</Eyebrow>
          <H2 id="marketplaces-heading">Marketplace Seller Setup and Enablement</H2>
          <p className="mt-3 text-xl font-semibold text-zinc-800">Get Ready to Sell on Amazon, Takealot and Makro</p>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Marketplaces can help businesses access customers beyond their own websites, but getting started requires preparation. Rekisa
            helps sellers understand marketplace requirements, prepare their product catalogues, develop listings and organise the systems
            needed to manage marketplace selling.
          </p>
        </div>
        <div className="mt-10 rounded-3xl bg-[#FBF7F2] border border-zinc-200 p-7"><MarketplaceLogos testid="ecom-logos-marketplaces" /></div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MP_SERVICES.map(([t, c], i) => (
            <div key={t} data-testid={`ecom-marketplace-service-${i}`} className="rounded-2xl border border-zinc-200 p-6">
              <h3 className="font-display text-lg font-black text-zinc-900">{t}</h3>
              <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{c}</p>
            </div>
          ))}
        </div>
        <div data-testid="ecom-marketplace-disclaimer" className="mt-8 rounded-2xl border border-amber-300/60 bg-amber-50 p-5 text-sm text-zinc-800 leading-relaxed">
          <span className="font-bold">Important: </span>Amazon, Takealot and Makro independently determine seller eligibility, approval and
          operating requirements — Rekisa cannot guarantee approval and is not a marketplace registration authority. Rekisa's implementation
          fees are separate from marketplace fees, subscriptions, commissions, advertising charges and fulfilment costs.
        </div>
        <div className="mt-8"><CtaLink label="Get Marketplace Setup Assistance" serviceKey="marketplace" location="ecom_marketplaces" testid="ecom-marketplaces-cta" /></div>
      </div>
    </section>
  );
}
